import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBooking } from '@features/booking/state';
import { useRestaurant } from '@core/tenant/TenantProvider';
import { isPaymentGuaranteeType } from '@features/booking/state/types';
import { resolveBillingCountry } from '@features/booking/utils/billingCountry';
import { getReceiptLabels, normalizeReceiptLang } from '@features/booking/utils/billingReceiptLabels';
import type { SupportedLanguage } from '@core/i18n/types';

const PERU = 'PE';
const ARGENTINA = 'AR';

export interface BillingReceiptInput {
  countryCode?: string | null;
  allowBillingReceiptType: boolean;
  allowFullBillingDetails: boolean;
  guaranteeType: string | null | undefined;
  lang: string;
  /** Per-restaurant text overrides, keyed by language then by i18n key. */
  appTexts?: Record<string, Record<string, string>>;
}

/** The four labels a venue can already override through appTexts today. */
const OVERRIDE_KEYS = {
  personalReceipt: 'PERSONAL_BILL',
  businessReceipt: 'BUSINESS_BILL',
  personalDocument: 'PERSONAL_ID_NUMBER',
  businessDocument: 'BUSINESS_TAX_ID',
} as const;

export interface ResolvedReceiptLabels {
  personalReceipt: string | null;
  businessReceipt: string | null;
  personalDocument: string | null;
  businessDocument: string | null;
}

export interface BillingReceiptDecision {
  /** Render the receipt selector and its fiscal fields. */
  showReceiptSelector: boolean;
  /** Render the generic billing address block. */
  showBillingAddress: boolean;
  /** Render the identity document field inside the generic block. */
  showDocumentField: boolean;
  country: string | null;
  lang: SupportedLanguage;
  /**
   * Final text for each label, or null where the caller should fall back to
   * the generic i18n key.
   */
  labels: ResolvedReceiptLabels;
}

/**
 * Decides which billing blocks belong on the payment step.
 *
 * Peru keeps an exemption: it saw the fiscal block before the flag existed and
 * must keep seeing it whether or not the flag is on. Everyone else opts in
 * through allowBillingReceiptType. Country only ever picks the wording — visibility
 * is the flag's job.
 *
 * The two blocks are mutually exclusive. The fiscal one already collects a
 * document and an address under country-correct labels, so showing the generic
 * one alongside it would ask for the same two things twice on the payment step,
 * where every extra field costs conversion. This is a deliberate departure from
 * the Angular widget, where both blocks appeared together.
 */
export function decideBillingReceipt(input: BillingReceiptInput): BillingReceiptDecision {
  const country = resolveBillingCountry(input.countryCode);
  const lang = normalizeReceiptLang(input.lang);
  const hasGuarantee = isPaymentGuaranteeType(input.guaranteeType);

  const showReceiptSelector = hasGuarantee && (country === PERU || input.allowBillingReceiptType);
  const showBillingAddress = hasGuarantee && input.allowFullBillingDetails && !showReceiptSelector;

  const table = getReceiptLabels(country);
  const overrides = input.appTexts?.[lang] ?? {};

  // What the venue configured wins over the country default, exactly as it
  // does for every other string in the widget. A Peruvian venue that replaced
  // "DNI" with "Passport or personal ID number" — because it serves tourists
  // who have no Peruvian DNI — keeps that wording.
  return {
    showReceiptSelector,
    showBillingAddress,
    // Argentina is the only country that asks for a bare identity document, and
    // it has to keep asking wherever it asks today. On staging the field rides
    // along with the generic address block, which an AR venue does render — so
    // unlike Peru, turning the receipt selector on here would take the field
    // away and stop populating identityDocument. It follows the venue rather
    // than the block: the address block when that is what shows, the receipt
    // block otherwise.
    showDocumentField: country === ARGENTINA && hasGuarantee && input.allowFullBillingDetails,
    country,
    lang,
    labels: {
      personalReceipt: overrides[OVERRIDE_KEYS.personalReceipt] ?? table?.personalReceipt[lang] ?? null,
      businessReceipt: overrides[OVERRIDE_KEYS.businessReceipt] ?? table?.businessReceipt[lang] ?? null,
      personalDocument: overrides[OVERRIDE_KEYS.personalDocument] ?? table?.personalDocument ?? null,
      businessDocument: overrides[OVERRIDE_KEYS.businessDocument] ?? table?.businessDocument ?? null,
    },
  };
}

export function useBillingReceipt(): BillingReceiptDecision {
  const { i18n } = useTranslation('booking');
  const booking = useBooking();
  const restaurant = useRestaurant();

  const { countryCode, allowBillingReceiptType, allowFullBillingDetails, appTexts } = restaurant;
  const { guaranteeType } = booking;
  const lang = i18n.language;

  // BillingSection re-renders on every keystroke in a billing field, so the
  // decision is memoised rather than recomputed per character.
  return useMemo(
    () =>
      decideBillingReceipt({
        countryCode,
        allowBillingReceiptType: Boolean(allowBillingReceiptType),
        allowFullBillingDetails: Boolean(allowFullBillingDetails),
        guaranteeType,
        lang,
        appTexts,
      }),
    [countryCode, allowBillingReceiptType, allowFullBillingDetails, guaranteeType, lang, appTexts],
  );
}
